/**
 * Client-safe schema and type definitions for review rate limits.
 *
 * This file is the shared contract between:
 * - server/runtime code that loads and persists config
 * - client/admin code that validates and edits config
 *
 * It intentionally avoids server-only imports (Redis, logger, etc.) so it can
 * be imported safely from client components and route validators.
 */

import { z } from 'zod';

import { MAX_PLAN_USAGE_MULTIPLIER } from '@/types/billing-plan';

// =============================================================================
// Review Limits Dashboard Filters
// =============================================================================

// Dashboard filters for reviewed-line state and billing/whitelist cohorts.
export const REVIEW_LIMITS_FILTER_KEYS = ['over', 'vip', 'trial', 'normal'] as const;
export type ReviewLimitsFilterKey = (typeof REVIEW_LIMITS_FILTER_KEYS)[number];

// Installations table sort keys. Every key maps to a rendered column so the
// active sort always matches what the table displays.
export const REVIEW_LIMITS_SORT_KEYS = [
  'monthlyLinePct',
  'monthlyLines',
  'reviewedCharsPerQuotaSeat',
  'seats',
] as const;
export type ReviewLimitsSortBy = (typeof REVIEW_LIMITS_SORT_KEYS)[number];
export const DEFAULT_REVIEW_LIMITS_SORT_BY: ReviewLimitsSortBy = 'monthlyLinePct';

function strictObject<T extends z.ZodRawShape>(shape: T) {
  return z.object(shape).strict();
}

const MAX_REVIEW_LINE_LIMIT = 1_000_000_000;
const MAX_FLEX_CAPACITY_BLOCK_PRICE_CENTS_OVERRIDE = 1_000_000_000;
const MAX_PUBLIC_REPOSITORY_SEAT_CAP = 10_000;
/**
 * Default included reviewed-line allowance per paid seat, per billing period.
 * The installation's monthly pool is `linesPerSeat * paidSeats` (scaled by the
 * trial multiplier for trials). Pre-cutoff Pro installs keep the higher
 * grandfathered allowance instead — see
 * `DEFAULT_GRANDFATHERED_PRO_REVIEW_LINES_PER_SEAT`.
 *
 * July 2026: raised from 20k/40k back to 40k/80k for everyone (the June 2026
 * reduction for new installs was reverted; nobody's limit goes down, and the
 * public-repo fair-use allowances derived from the Team value intentionally
 * double along with it).
 */
export const DEFAULT_TEAM_REVIEW_LINES_PER_SEAT = 40_000;
export const DEFAULT_PRO_REVIEW_LINES_PER_SEAT = 80_000;
export const DEFAULT_MAX_REVIEW_LINES_PER_SEAT = Math.floor(
  DEFAULT_PRO_REVIEW_LINES_PER_SEAT * MAX_PLAN_USAGE_MULTIPLIER,
);
/**
 * Reviewed lines granted per purchased flex capacity block. Deliberately
 * smaller than a seat's monthly allowance: overages are usually a fraction of
 * a seat, and a fine-grained block keeps the automatic marginal charge small.
 * The top-up buys the whole deficit in one charge, so small blocks do not
 * multiply the number of invoices.
 */
export const FLEX_CAPACITY_BLOCK_LINES = 10_000;
/**
 * Flat price per flex capacity block, identical on every plan and billing
 * interval ($2.00 per 1,000 reviewed lines). Deliberately NOT derived from the
 * subscription's seat price: seat rates differ per plan (Team $40/40k vs Pro
 * $99/80-100k lines), which made seat-priced flex blocks cost 2-2.5x more per
 * line on Pro.
 *
 * July 2026: reduced from $3.00/1k to $2.00/1k after the June cost
 * optimizations lowered the cost to serve to ~$1.5/1k. The rate still sits
 * at/above the included per-seat marginal rate on both plans so overage is
 * never cheaper than included usage.
 *
 * Blocks exist only at purchase time; stored customer data (the spend cap,
 * the purchase ledger's amounts) is denominated in cents so block size and
 * price can change without data migrations.
 */
export const FLEX_CAPACITY_BLOCK_PRICE_CENTS = 2_000;
/** Display-level dollar value of `FLEX_CAPACITY_BLOCK_PRICE_CENTS`. */
export const FLEX_CAPACITY_BLOCK_PRICE_DOLLARS = FLEX_CAPACITY_BLOCK_PRICE_CENTS / 100;
/**
 * Effective flex block price for an installation. Per-install overrides exist
 * only to DISCOUNT below the global default (e.g. the June 2026 $25
 * grandfather written when the price rose $25 -> $30), so a global price DROP
 * must apply to everyone immediately: an override can never pin an
 * installation above the current list price.
 */
export function getEffectiveFlexCapacityBlockPriceCents(
  overridePriceCents?: number | null,
): number {
  if (
    typeof overridePriceCents === 'number' &&
    Number.isInteger(overridePriceCents) &&
    overridePriceCents > 0
  ) {
    return Math.min(overridePriceCents, FLEX_CAPACITY_BLOCK_PRICE_CENTS);
  }
  return FLEX_CAPACITY_BLOCK_PRICE_CENTS;
}
/**
 * Soft-alert threshold: email admins when an uncapped window's flex spend
 * crosses this many cents ($200). Cents-denominated so the threshold keeps
 * its dollar meaning across block size/price changes.
 */
export const FLEX_CAPACITY_UNCAPPED_ALERT_CENTS = 20_000;
export const DEFAULT_GRANDFATHERED_PRO_REVIEW_LINES_PER_SEAT = 100_000;
export const DEFAULT_PRO_REVIEW_LINE_GRANDFATHERED_INSTALL_CUTOFF_STARTS_AT =
  '2026-05-26T00:00:00.000Z';
// July 2026: the June 2026 one-time quota reduction (Team 40k->20k, Pro
// 80k->40k for new installs) was reverted — the legacy constants and the
// 2026-06-16 cutoff are gone because current defaults equal the old legacy
// values again, so every install resolves through the live config.
//
// Public-repo fair-use allowances are min(seats, cap) x the Team per-seat
// value; the July 2026 raise intentionally doubled them (org ceiling
// 200k -> 400k, personal 40k -> 80k) with the caps left unchanged.
const DEFAULT_PUBLIC_REPOSITORY_ORG_SEAT_CAP = 10;
const DEFAULT_PUBLIC_REPOSITORY_USER_SEAT_CAP = 2;

const boundedLineLimitSchema = z
  .number()
  .finite()
  .int()
  .min(0, { message: 'must be at least 0' })
  .max(MAX_REVIEW_LINE_LIMIT, { message: `must be at most ${MAX_REVIEW_LINE_LIMIT}` });
const flexCapacityBlockPriceCentsOverrideSchema = z
  .number()
  .finite()
  .int()
  .min(1, { message: 'must be at least 1 cent' })
  .max(MAX_FLEX_CAPACITY_BLOCK_PRICE_CENTS_OVERRIDE, {
    message: `must be at most ${MAX_FLEX_CAPACITY_BLOCK_PRICE_CENTS_OVERRIDE} cents`,
  });

export const reviewLineLimitEnforcementModeSchema = z.enum(['shadow', 'enforced']);
export type ReviewLineLimitEnforcementMode = z.infer<typeof reviewLineLimitEnforcementModeSchema>;

export const REVIEW_LINE_LIMIT_OVERRIDE_SCOPE = {
  PERMANENT: 'permanent',
  BILLING_PERIOD: 'billing_period',
} as const;
export const REVIEW_LINE_LIMIT_OVERRIDE_SCOPES = [
  REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT,
  REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.BILLING_PERIOD,
] as const;
export const reviewLineLimitOverrideScopeSchema = z.enum(REVIEW_LINE_LIMIT_OVERRIDE_SCOPES);
export type ReviewLineLimitOverrideScope = z.infer<typeof reviewLineLimitOverrideScopeSchema>;

export type ReviewLineLimitOverrideScopeStatus =
  | 'permanent'
  | 'active_billing_period'
  | 'inactive_billing_period';

const reviewedLineOverridePeriodBoundarySchema = z.string().datetime({
  offset: false,
  local: false,
  precision: 3,
});

export const reviewLineLimitConfigSchema = strictObject({
  monthlyLinesPerSeat: boundedLineLimitSchema,
  proMonthlyLinesPerSeat: boundedLineLimitSchema,
  grandfatheredProMonthlyLinesPerSeat: boundedLineLimitSchema,
  proGrandfatheredInstallCutoffStartsAt: z.string().datetime({
    offset: false,
    local: false,
    precision: 3,
  }),
  trialMultiplier: z.number().finite().min(0).max(1),
  warningThreshold: z.number().finite().min(0).max(1),
  enforcementMode: reviewLineLimitEnforcementModeSchema,
  publicRepositoryOrgSeatCap: z.number().finite().int().min(1).max(MAX_PUBLIC_REPOSITORY_SEAT_CAP),
  publicRepositoryUserSeatCap: z.number().finite().int().min(1).max(MAX_PUBLIC_REPOSITORY_SEAT_CAP),
});

export type ReviewLineLimitConfig = z.infer<typeof reviewLineLimitConfigSchema>;

export const DEFAULT_REVIEW_LINE_LIMITS: ReviewLineLimitConfig = {
  monthlyLinesPerSeat: DEFAULT_TEAM_REVIEW_LINES_PER_SEAT,
  proMonthlyLinesPerSeat: DEFAULT_PRO_REVIEW_LINES_PER_SEAT,
  grandfatheredProMonthlyLinesPerSeat: DEFAULT_GRANDFATHERED_PRO_REVIEW_LINES_PER_SEAT,
  proGrandfatheredInstallCutoffStartsAt:
    DEFAULT_PRO_REVIEW_LINE_GRANDFATHERED_INSTALL_CUTOFF_STARTS_AT,
  trialMultiplier: 0.5,
  warningThreshold: 0.9,
  enforcementMode: 'enforced',
  publicRepositoryOrgSeatCap: DEFAULT_PUBLIC_REPOSITORY_ORG_SEAT_CAP,
  publicRepositoryUserSeatCap: DEFAULT_PUBLIC_REPOSITORY_USER_SEAT_CAP,
} as const;

/**
 * Dynamic review rate-limit configuration.
 *
 * Only the reviewed-line policy is configurable.
 */
export const dynamicRateLimitConfigSchema = strictObject({
  reviewLineLimits: reviewLineLimitConfigSchema,
});

export type DynamicRateLimitConfig = z.infer<typeof dynamicRateLimitConfigSchema>;

export const dynamicRateLimitConfigUpdatesSchema = strictObject({
  reviewLineLimits: reviewLineLimitConfigSchema.partial().optional(),
});

export type DynamicRateLimitConfigUpdates = z.infer<typeof dynamicRateLimitConfigUpdatesSchema>;

// =============================================================================
// Super Admin API Types
// =============================================================================
// Types for the review-limits super admin endpoints.
// Shared between API routes and dashboard components.
// =============================================================================

export type InstallationEntry = {
  installationId: number;
  owner: string;
  payingSeatCount: number;
  isWhitelisted: boolean;
};

export type InstallationsResponse = {
  installations: InstallationEntry[];
  whitelistedCount: number;
};

// =============================================================================
// Installation Reviewed-Line Override Types
// =============================================================================

/**
 * Stored per-installation override for reviewed-line capacity.
 *
 * `monthlyLinesPerSeat: null` keeps the global default.
 * `flexCapacityBlockPriceCents: null` keeps the global flex block price; a
 * non-null value can only DISCOUNT —
 * resolution clamps it to min(override, global) via
 * `getEffectiveFlexCapacityBlockPriceCents`, so global price drops reach
 * every installation immediately.
 */
const installationReviewLineLimitOverrideObjectSchema = z.object({
  monthlyLinesPerSeat: boundedLineLimitSchema.nullable(),
  flexCapacityBlockPriceCents: flexCapacityBlockPriceCentsOverrideSchema.nullable().optional(),
  scope: reviewLineLimitOverrideScopeSchema.optional(),
  periodStart: reviewedLineOverridePeriodBoundarySchema.optional(),
  periodEnd: reviewedLineOverridePeriodBoundarySchema.optional(),
  notes: z.string().optional(),
  updatedAt: z.string(),
});

function validateInstallationReviewLineLimitOverride(
  value: z.infer<typeof installationReviewLineLimitOverrideObjectSchema>,
  ctx: z.RefinementCtx,
) {
  const scope = value.scope ?? REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT;

  if (scope === REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT) {
    if (value.periodStart !== undefined || value.periodEnd !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'permanent reviewed-line overrides must not include period boundaries',
        path: ['scope'],
      });
    }
    return;
  }

  if (value.periodStart === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'billing-period reviewed-line overrides require periodStart',
      path: ['periodStart'],
    });
  }

  if (value.periodEnd === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'billing-period reviewed-line overrides require periodEnd',
      path: ['periodEnd'],
    });
  }

  if (value.periodStart !== undefined && value.periodEnd !== undefined) {
    const periodStartMs = Date.parse(value.periodStart);
    const periodEndMs = Date.parse(value.periodEnd);
    if (
      Number.isFinite(periodStartMs) &&
      Number.isFinite(periodEndMs) &&
      periodStartMs >= periodEndMs
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'periodEnd must be after periodStart',
        path: ['periodEnd'],
      });
    }
  }
}

export const installationReviewLineLimitOverrideSchema =
  installationReviewLineLimitOverrideObjectSchema
    .strict()
    .superRefine(validateInstallationReviewLineLimitOverride);
const storedInstallationReviewLineLimitOverrideSchema =
  installationReviewLineLimitOverrideObjectSchema
    .strip()
    .superRefine(validateInstallationReviewLineLimitOverride);
export type InstallationReviewLineLimitOverride = z.infer<
  typeof installationReviewLineLimitOverrideSchema
>;

/** Parse the current reviewed-line override fields from stored configuration. */
export function parseInstallationReviewLineLimitOverride(
  value: unknown,
): InstallationReviewLineLimitOverride | null {
  const result = storedInstallationReviewLineLimitOverrideSchema.safeParse(value);
  return result.success ? result.data : null;
}

function doStoredPeriodBoundariesMatchWindow(
  override: Pick<InstallationReviewLineLimitOverride, 'periodStart' | 'periodEnd'>,
  quotaWindow: { start: Date; end: Date } | null,
): boolean {
  if (!quotaWindow || override.periodStart === undefined || override.periodEnd === undefined) {
    return false;
  }

  return (
    Date.parse(override.periodStart) === quotaWindow.start.getTime() &&
    Date.parse(override.periodEnd) === quotaWindow.end.getTime()
  );
}

export function isReviewLineLimitOverrideActiveForWindow(
  override: InstallationReviewLineLimitOverride | null | undefined,
  quotaWindow: { start: Date; end: Date } | null,
): override is InstallationReviewLineLimitOverride {
  if (!override) {
    return false;
  }

  if (
    (override.scope ?? REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT) ===
    REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT
  ) {
    return true;
  }

  return doStoredPeriodBoundariesMatchWindow(override, quotaWindow);
}

export function getReviewLineLimitOverrideScopeStatus(
  override: InstallationReviewLineLimitOverride | null | undefined,
  quotaWindow: { start: Date; end: Date } | null,
): ReviewLineLimitOverrideScopeStatus | null {
  if (!override) {
    return null;
  }

  if (
    (override.scope ?? REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT) ===
    REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT
  ) {
    return 'permanent';
  }

  return doStoredPeriodBoundariesMatchWindow(override, quotaWindow)
    ? 'active_billing_period'
    : 'inactive_billing_period';
}

export function isEmptyReviewLineLimitOverride(
  value:
    | Pick<
        InstallationReviewLineLimitOverride,
        'monthlyLinesPerSeat' | 'flexCapacityBlockPriceCents' | 'scope' | 'notes'
      >
    | null
    | undefined,
): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  // Notes are metadata on a real override; a notes-only payload should not
  // create a no-op override row in Redis.
  return value.monthlyLinesPerSeat === null && value.flexCapacityBlockPriceCents == null;
}

// =============================================================================
// Review Limits Dashboard Types
// =============================================================================

export type ReviewLimitsRepoVisibilityMode = 'public_only' | 'private_only' | 'mixed';

/** Reviewed-line limit state for one installation on the Review Limits page. */
export type ReviewLimitsInstallationEntry = {
  installationId: number;
  owner: string;
  accountType: string;
  installationCreatedAt: string;
  /** Assigned/enabled paid seats currently in use. */
  payingSeatCount: number;
  /** Seat count used for quota scaling before unpaid visibility caps are applied. */
  quotaSeatCount: number;
  /** Final seat denominator used by reviewed-line limits. */
  effectiveQuotaSeatCount: number;
  repoCount: number;
  publicRepoCount: number;
  privateRepoCount: number;
  repoVisibilityMode: ReviewLimitsRepoVisibilityMode;
  contributorCount: number;
  isWhitelisted: boolean;
  /** Whether installation is on Stripe trial (reviewed-line trial multiplier applies) */
  isTrialling: boolean;
  /** Whether installation is on Stripe Pro plan (Pro reviewed-line allowance applies) */
  isOnProPlan: boolean;
  reviewLineLimitOverride: InstallationReviewLineLimitOverride | null;
  reviewLineLimitOverrideScopeStatus: ReviewLineLimitOverrideScopeStatus | null;
  // Current-month general-review character metrics, deduped by review session.
  reviewedCharsMonthly: number;
  reviewedCharsPerQuotaSeatMonthly: number;
  // Current reviewed-line quota metrics, deduped by review session.
  reviewedLinesMonthly: number;
  reviewSessionsMonthly: number;
  reviewedLinesInstallationQuotaMonthly: number;
  reviewSessionsInstallationQuotaMonthly: number;
  reviewedLinesPublicRepositoryFairUseMonthly: number;
  reviewSessionsPublicRepositoryFairUseMonthly: number;
  publicRepositoryFairUseMonthlyLineLimit: number | null;
  publicRepositoryFairUseMonthlyLinePercentUsed: number;
  publicRepositoryFairUseMonthlyLineExceeded: boolean;
  reviewedLinesTotalMonthly: number;
  reviewSessionsTotalMonthly: number;
  monthlyLineLimit: number | null;
  monthlyLinePercentUsed: number;
  monthlyLineExceeded: boolean;
  lineEnforcementMode: ReviewLineLimitEnforcementMode;
  lineAppliesToInstallation: boolean;
};

/** Aggregate reviewed-line state across the installations on the Review Limits page. */
export type ReviewLimitsStats = {
  totalInstallations: number;
  whitelistedCount: number;
  /**
   * Installations whose active reviewed-line bucket is over its limit and
   * that are subject to line limits (not whitelisted/exempt).
   */
  overLimitCount: number;
  /** Reviewed lines across every listed installation and quota bucket this period. */
  reviewedLinesMonthly: number;
};

export type ReviewLimitsPagination = {
  limit: number;
  offset: number;
  totalCount: number;
  hasMore: boolean;
};

export type ReviewLimitsSummaryResponse = {
  stats: ReviewLimitsStats;
};

export type ReviewLimitsEntriesResponse = {
  entries: ReviewLimitsInstallationEntry[];
  pagination: ReviewLimitsPagination;
};

// =============================================================================
// Overrides API Types
// =============================================================================

export type InstallationOverrideEntry = {
  installationId: number;
  owner: string;
  reviewLineLimitOverride: InstallationReviewLineLimitOverride | null;
  reviewLineLimitOverrideScopeStatus: ReviewLineLimitOverrideScopeStatus | null;
};

export type OverridesResponse = {
  entries: InstallationOverrideEntry[];
  globalReviewLineLimits: {
    monthlyLinesPerSeat: number;
  };
};

const overrideInstallationIdSchema = z
  .number()
  .int()
  .positive('installationId must be a positive integer');

const reviewLineLimitOverridePayloadSchema = z
  .object({
    monthlyLinesPerSeat: boundedLineLimitSchema.nullable().optional(),
    flexCapacityBlockPriceCents: flexCapacityBlockPriceCentsOverrideSchema.nullable().optional(),
    scope: reviewLineLimitOverrideScopeSchema.optional(),
    notes: z.string().optional(),
  })
  .transform((value) => ({
    monthlyLinesPerSeat: value.monthlyLinesPerSeat ?? null,
    flexCapacityBlockPriceCents: value.flexCapacityBlockPriceCents ?? null,
    scope: value.scope ?? REVIEW_LINE_LIMIT_OVERRIDE_SCOPE.PERMANENT,
    ...(value.notes !== undefined ? { notes: value.notes } : {}),
  }));

export type ReviewLineLimitOverridePayload = z.infer<typeof reviewLineLimitOverridePayloadSchema>;

const optionalReviewLineLimitOverridePayloadSchema = z
  .union([reviewLineLimitOverridePayloadSchema, z.null()])
  .optional();

export const ReviewLimitsOverrideUpdateRequestSchema = strictObject({
  installationId: overrideInstallationIdSchema,
  reviewLineLimitOverride: optionalReviewLineLimitOverridePayloadSchema,
});
